import React from "react";
import { MASCOT } from "../../brand";

/**
 * <Mascot>
 * Renders the canonical Gadget Guy mascot in one of the approved poses.
 *
 * Uses <picture> with WebP + srcset for retina performance. The images are
 * cropped from owner-approved artwork — do not restyle the character.
 *
 * pose options: heroDefault | thumbsUp | pointing | presenting |
 *               welcoming | laptop | tablet | waving
 *
 * size options (approximate rendered height):
 *   sm  = 120px   — inline accent
 *   md  = 200px   — section accent
 *   lg  = 320px   — feature placement
 *   xl  = 480px   — hero
 *   custom via `height` prop overrides.
 *
 * Notes:
 *  - Below-fold placements should pass loading="lazy"
 *  - Hero placement should pass priority (loading="eager", fetchPriority="high")
 *  - The mascot assets keep their supplied backgrounds. If a section needs
 *    a clean transparent-background render, request an approved isolated
 *    render for that specific pose — do NOT background-remove here.
 */
const SIZE_MAP = { sm: 120, md: 200, lg: 320, xl: 480 };

const ALT_TEXT = {
  heroDefault: "Gadget Guy",
  thumbsUp: "Gadget Guy giving a thumbs up",
  pointing: "Gadget Guy pointing",
  presenting: "Gadget Guy presenting",
  welcoming: "Gadget Guy welcoming you",
  laptop: "Gadget Guy at a laptop",
  tablet: "Gadget Guy with a tablet",
  waving: "Gadget Guy waving",
};

export const Mascot = ({
  pose = "heroDefault",
  size = "md",
  height,
  alt,
  className = "",
  style = {},
  loading = "lazy",
  fetchPriority,
  ...rest
}) => {
  const asset = MASCOT[pose] ?? MASCOT.heroDefault;
  const h = height ?? SIZE_MAP[size] ?? SIZE_MAP.md;
  return (
    <picture data-testid={`ggm-mascot-${pose}`}>
      <source type="image/webp" srcSet={`${asset.webp} 1x, ${asset.webp2x} 2x`} />
      <img
        src={asset.png}
        srcSet={`${asset.png} 1x, ${asset.png2x} 2x`}
        alt={alt ?? ALT_TEXT[pose] ?? "Gadget Guy"}
        height={h}
        className={className}
        style={{ height: h, width: "auto", display: "block", ...style }}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        draggable={false}
        {...rest}
      />
    </picture>
  );
};

export default Mascot;
